import { useQuery } from "@tanstack/react-query";
import { apiClient } from "src/lib/apiClient";
import { keys } from "./keys";
import { LoyaltyStats, mapLoyaltyStats } from "./mappers";

/**
 * Per-location loyalty data behind the analytics dashboard.
 *
 * Every hook here is keyed on `merchantId`, which is what makes the old
 * `merchantId`/`loaded`/`error` bookkeeping in `statsSlice` unnecessary: that
 * state existed to detect "the data in the store belongs to a different
 * location", and a per-location key makes the situation unrepresentable.
 */

/**
 * GET loyalty/stats — the location's whole history in one payload, so the period
 * control on the analytics page costs no request.
 */
export const useLoyaltyStats = (merchantId: number) =>
  useQuery<LoyaltyStats>({
    queryKey: keys.loyalty.stats(merchantId),
    enabled: merchantId > 0,
    queryFn: async () => {
      const response = await apiClient.get("loyalty/stats", {
        params: { merchantId },
      });
      return mapLoyaltyStats(response.data);
    },
  });

/** Raw row from GET loyalty/user-stats — lifetime per-customer totals. */
export interface UserStatsRow {
  id: number;
  first_name: string;
  last_name: string;
  total_purchases: number;
  total_rewards_issued: number;
  total_rewards_redeemed: number;
  current_total: number;
  current_streak: number;
}

/**
 * Deliberately not period-filtered: "who are my best customers" is not a
 * question about a window.
 */
export const useUserStats = (merchantId: number) =>
  useQuery<UserStatsRow[]>({
    queryKey: keys.loyalty.userStats(merchantId),
    enabled: merchantId > 0,
    queryFn: async () => {
      const response = await apiClient.get("loyalty/user-stats", {
        params: { merchantId },
      });
      return Array.isArray(response.data) ? response.data : [];
    },
  });

/**
 * GET loyalty/recent-purchases. Pass no dates for "all time"; the range is part
 * of the key so flipping period and back is served from cache.
 */
export const useRecentPurchases = <T,>(
  merchantId: number,
  startDate?: string,
  endDate?: string
) =>
  useQuery<T[]>({
    queryKey: keys.loyalty.recentPurchases(merchantId, startDate, endDate),
    enabled: merchantId > 0,
    queryFn: async () => {
      const params: Record<string, string | number> = { merchantId };
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const response = await apiClient.get("loyalty/recent-purchases", { params });
      return Array.isArray(response.data) ? response.data : [];
    },
  });

/**
 * GET loyalty/favourite-users/:merchantId — the wallet-saves count. Two
 * consumers share this key: the analytics ImpactCards tile and the digital
 * flyers page, which uses the same number to size a campaign.
 */
export const useFavouriteUsers = (merchantId: number) =>
  useQuery<unknown[]>({
    queryKey: keys.loyalty.favouriteUsers(merchantId),
    enabled: merchantId > 0,
    queryFn: async () => {
      const response = await apiClient.get(
        `loyalty/favourite-users/${merchantId}`
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
